import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Check, Gamepad2, ListChecks, PartyPopper, PlayCircle } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, ApiError } from '../../lib/api';
import type { CompletionPayload, LessonPayload, Stage, StudentHome } from '../../lib/types';
import { ButtonLink, Notice, Spinner } from '../../ui';
import { StudentShell } from './StudentShell';
import { AssessmentStage } from './stages/AssessmentStage';
import { CelebrationStage } from './stages/CelebrationStage';
import { LabStage } from './stages/LabStage';
import { VideoStage } from './stages/VideoStage';

const STEPS = [
  { stage: 'video', label: 'Watch', icon: PlayCircle },
  { stage: 'lab', label: 'Play', icon: Gamepad2 },
  { stage: 'assessment', label: 'Quiz', icon: ListChecks },
  { stage: 'complete', label: 'Celebrate', icon: PartyPopper },
] as const;
const ORDER: Stage[] = ['video', 'lab', 'assessment', 'complete'];

function Stepper({ current }: { current: Stage }) {
  const at = ORDER.indexOf(current);
  return (
    <ol className="flex items-center gap-1.5 sm:gap-3" aria-label="Lesson steps">
      {STEPS.map((s, i) => {
        const state = i < at ? 'done' : i === at ? 'current' : 'todo';
        return (
          <li key={s.stage} className="flex items-center gap-1.5 sm:gap-3" aria-current={state === 'current' ? 'step' : undefined}>
            <span
              className={clsx(
                'flex h-10 items-center gap-2 rounded-full px-3 font-display text-sm font-bold transition-colors sm:px-4',
                state === 'done' && 'bg-mint-soft text-mint',
                state === 'current' && 'bg-brand text-white',
                state === 'todo' && 'bg-line/60 text-muted',
              )}
            >
              {state === 'done' ? <Check className="size-4" strokeWidth={3} aria-hidden /> : <s.icon className="size-4" aria-hidden />}
              <span className={clsx(state !== 'current' && 'hidden md:inline')}>{s.label}</span>
            </span>
            {i < STEPS.length - 1 && <span className={clsx('h-0.5 w-3 rounded md:w-6', i < at ? 'bg-mint' : 'bg-line-strong')} aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

export function LessonPage() {
  const { lessonId = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: () => api.get<LessonPayload>(`/student/lessons/${lessonId}`),
    staleTime: Infinity,
  });
  const home = useQuery({ queryKey: ['student-home'], queryFn: () => api.get<StudentHome>('/student/home') });

  const [stageOverride, setStageOverride] = useState<Stage | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CompletionPayload | null>(null);

  const lesson = q.data;
  const stage: Stage = stageOverride ?? lesson?.progress.stage ?? 'video';
  const rev = revision ?? lesson?.progress.revision ?? 0;

  const advance = useCallback(
    async (to: 'lab' | 'assessment', expectedRevision: number) => {
      setAdvancing(true);
      setError('');
      try {
        const r = await api.post<{ stage: Stage; revision: number }>(`/student/lessons/${lessonId}/advance`, { to, expectedRevision });
        setRevision(r.revision);
        setStageOverride(r.stage);
        // Items and attempts change between stages; refetch quietly for the next stage.
        await qc.invalidateQueries({ queryKey: ['lesson', lessonId] });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        if (e instanceof ApiError && e.code === 'PROGRESS_REVISION_CONFLICT') {
          setStageOverride(null);
          setRevision(null);
          await q.refetch();
        } else setError(e instanceof ApiError ? e.message : 'Something went wrong.');
      } finally {
        setAdvancing(false);
      }
    },
    [lessonId, q, qc],
  );

  const onGameDone = useCallback((r: number) => void advance('assessment', r), [advance]);

  if (q.isLoading) return <StudentShell><Spinner label="Opening your lesson" /></StudentShell>;
  if (q.isError || !lesson)
    return (
      <StudentShell>
        <div className="mx-auto max-w-3xl px-4 py-10">
          <Notice tone="error">{q.error instanceof ApiError ? q.error.message : 'We could not open this lesson.'}</Notice>
          <ButtonLink to="/learn" className="mt-4" variant="secondary">Back to my lessons</ButtonLink>
        </div>
      </StudentShell>
    );

  const lessons = home.data?.lessons ?? [];
  const idx = lessons.findIndex((l) => l.lessonVersionId === lessonId);
  const nextLessonId = idx >= 0 ? lessons[idx + 1]?.lessonVersionId : undefined;

  return (
    <StudentShell stars={home.data?.totals.stars}>
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Link to="/learn" aria-label="Back to my lessons" className="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface shadow-sm hover:text-brand">
              <ArrowLeft className="size-5" aria-hidden />
            </Link>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-muted">Lesson {lesson.lesson.code.replace(/^C\d+-S/, '')}</p>
              <h1 className="text-2xl font-extrabold sm:text-3xl">{lesson.lesson.title}</h1>
            </div>
          </div>
          <Stepper current={stage} />
        </div>

        {error && <div className="mt-4"><Notice tone="error">{error}</Notice></div>}

        <AnimatePresence mode="wait">
          <motion.section
            key={stage}
            className="mt-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
          >
            {stage === 'video' && <VideoStage lesson={lesson} advancing={advancing} onFinished={() => void advance('lab', rev)} />}
            {stage === 'lab' && (q.isFetching ? <Spinner label="Getting the game ready" /> : <LabStage lesson={lesson} onFinished={onGameDone} />)}
            {stage === 'assessment' && (q.isFetching ? <Spinner label="Getting your quiz ready" /> : <AssessmentStage lesson={lesson} onComplete={(r) => { setResult(r); setStageOverride('complete'); void qc.invalidateQueries({ queryKey: ['student-home'] }); }} />)}
            {stage === 'complete' && (result ? <CelebrationStage result={result} nextLessonId={nextLessonId} /> : <ReplayComplete lessonId={lessonId} onResult={setResult} />)}
          </motion.section>
        </AnimatePresence>
      </main>
    </StudentShell>
  );
}

/** Reopening a finished lesson shows its celebration again (no new rewards). */
function ReplayComplete({ lessonId, onResult }: { lessonId: string; onResult: (r: CompletionPayload) => void }) {
  const q = useQuery({
    queryKey: ['lesson-complete', lessonId],
    queryFn: () => api.post<CompletionPayload>(`/student/lessons/${lessonId}/complete`),
  });
  if (q.data) {
    queueMicrotask(() => onResult(q.data));
  }
  return q.isError ? <Notice tone="error">We could not load your results.</Notice> : <Spinner label="Loading your stars" />;
}
